import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from './authSlice';

// Changed prices auto-expire after 1 hour
export const PRICE_CHANGE_TTL_MS = 60 * 60 * 1000;

export interface ChangedPriceEntry {
  price: number;
  changedAt: number;
}

interface ReturnPriceChangeState {
  allowChangePrice: boolean;
  // Changed prices are scoped PER CUSTOMER: { [customerId]: { [itemNumber]: entry } }
  changedPrices: { [customerId: string]: { [itemNumber: string]: ChangedPriceEntry } };
}

const initialState: ReturnPriceChangeState = {
  allowChangePrice: false,
  changedPrices: {},
};

const returnPriceChangeSlice = createSlice({
  name: 'returnPriceChange',
  initialState,
  reducers: {
    setAllowChangePrice: (state, action: PayloadAction<boolean>) => {
      state.allowChangePrice = action.payload;
      // Scenario 3: admin turned the permission off - wipe every customer's
      // changed prices so re-enabling starts fresh
      if (!action.payload) {
        state.changedPrices = {};
      }
    },
    setChangedPrice: {
      reducer: (
        state,
        action: PayloadAction<{ customerId: string; itemNumber: string; price: number; changedAt: number }>
      ) => {
        const { customerId, itemNumber, price, changedAt } = action.payload;
        if (!customerId) return;
        if (!state.changedPrices[customerId]) {
          state.changedPrices[customerId] = {};
        }
        const existing = state.changedPrices[customerId][itemNumber];
        // Update the existing entry in place (keep the original changedAt so the
        // 1 hour expiry runs from the first change); create it only when new.
        state.changedPrices[customerId][itemNumber] = {
          price,
          changedAt: existing?.changedAt ?? changedAt,
        };
      },
      prepare: (payload: { customerId: string; itemNumber: string; price: number }) => ({
        payload: { ...payload, changedAt: Date.now() },
      }),
    },
    removeChangedPrice: (state, action: PayloadAction<{ customerId: string; itemNumber: string }>) => {
      const { customerId, itemNumber } = action.payload;
      if (state.changedPrices[customerId]) {
        delete state.changedPrices[customerId][itemNumber];
      }
    },
    // Scenario 1 + 2: after placing a return order, clear ONLY that customer's
    // changed prices (pass the customerId). Without a payload, clears everything.
    clearChangedPrices: (state, action: PayloadAction<string | undefined>) => {
      const customerId = action.payload;
      if (customerId) {
        delete state.changedPrices[customerId];
      } else {
        state.changedPrices = {};
      }
    },
    clearExpiredChangedPrices: (state, action: PayloadAction<number>) => {
      const now = action.payload;
      Object.keys(state.changedPrices).forEach((customerId) => {
        const customerPrices: any = state.changedPrices[customerId];
        // Drop malformed buckets (e.g. entries persisted by an older flat structure)
        if (!customerPrices || typeof customerPrices !== 'object') {
          delete state.changedPrices[customerId];
          return;
        }
        Object.keys(customerPrices).forEach((itemNumber) => {
          const entry = customerPrices[itemNumber];
          if (!entry || typeof entry.changedAt !== 'number' || now - entry.changedAt > PRICE_CHANGE_TTL_MS) {
            delete customerPrices[itemNumber];
          }
        });
        if (Object.keys(customerPrices).length === 0) {
          delete state.changedPrices[customerId];
        }
      });
    },
  },
  extraReducers: (builder) => {
    // Clear everything on logout
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setAllowChangePrice,
  setChangedPrice,
  removeChangedPrice,
  clearChangedPrices,
  clearExpiredChangedPrices,
} = returnPriceChangeSlice.actions;

export default returnPriceChangeSlice.reducer;
